import { AfterViewInit, Component, OnDestroy, ViewChild, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { merge } from 'rxjs';
import { tap } from 'rxjs/operators';

import { HomeService } from '../services/home.service';
import { Quote } from '../../shared/models/quote.interface';
import { DashboardSummary } from '../dashboard.summary.interface';
import { MonthType } from '../../shared/models/month.type.interface';
import {RolePermission } from '../../shared/models/role.permission.enum';

import { AbstractBiditComponent } from '../../shared/controls/abstract.bidit.component';
import { DeviceDetectorService } from 'ngx-device-detector';
import { HomesDataSource } from '../datasources/homes-data-source';
import { LeadFilter } from '../../lead/models/lead.filter';
import { Customer } from '../../shared/models/customer.interface';
import { User } from '../../shared/models/user.interface';
import { ReleaseSettings } from '../../release/models/releaseSettings.interface';

export interface DealerHomeSummary {
  leadCount: number;
  salespersons: User[];
  customers: Customer[];
}

@Component({
  selector: 'app-home-desktop',
  templateUrl: './home-desktop.component.html',
  styleUrls: ['./home-desktop.component.scss'],
})
export class HomeDesktopComponent extends AbstractBiditComponent implements OnInit, AfterViewInit, OnDestroy {
  notificationMessage: any;
  dataSource: HomesDataSource;
  displayedColumns: string[] =
    ['projectName', 'projectNbr', 'customerName', 'contactName', 'customerPhone', 'expirationDate', 'salesperson'];

  @ViewChild(MatPaginator, { static: false }) set paginatorContent(paginator: MatPaginator) {
    this.dataSource.paginator = paginator;
  }
  @ViewChild(MatSort, { static: false }) set sortContent(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  summarySection: DealerHomeSummary;
  summary: DashboardSummary = {} as DashboardSummary;
  leads: Quote[] = new Array();
  
  years: number[] = new Array();
  months: MonthType[] = new Array();

  defaultDate: boolean = true;
  showIDI: boolean = false;
  quoteOnly: boolean = false;
  canAddLead: boolean = false;
  isSalesperson: boolean = false;

  currentMonth: boolean = true;
  customYear: number;
  customMonth: number;

  leadFilter: LeadFilter =
    {
      pastDue: false, customerId: null, jobTypeId: null, probability: '',
      salespersonId: '', projectName: '', dateRange: null, leadStart: null, leadEnd: null
    };

  constructor(deviceDetectorService: DeviceDetectorService, protected homeService: HomeService, protected router: Router) {
    super(deviceDetectorService);    
  }  

  ngOnInit() {
    this.dataSource = new HomesDataSource(this.homeService);

    this.showIDI = (HomeService._authNavStatusSource.value == "IDI");
    this.quoteOnly = (HomeService._authUserSource.value.isQuoteOnly);
    this.canAddLead = HomeService.hasPermission(RolePermission.AddLead);
    this.isSalesperson = HomeService.hasPermission(RolePermission.SalesPerson);
    let isFieldTech = HomeService.hasPermission(RolePermission.FieldTechnician);

    if (isFieldTech) this.router.navigate(['/job']);
  };

  ngAfterViewInit(): void {
    if (!this.showIDI) {
      this.setupDates();

      if (this.isSalesperson) {
        let user = HomeService._authUserSource.value;
        this.leadFilter.salespersonId = user.id;
      }

      this.loadSummary();
      this.loadDashboardSummary();
      this.loadLeads();
      this.getReleaseNotification();
    }

    // only wire up dealer home page when in dealer mode (ignore IDI mode)
    if (this.dataSource.sort != null) {
      // reset the paginator after sorting
      this.dataSource.sort.sortChange.subscribe(() => this.dataSource.paginator.pageIndex = 0);

      merge(this.dataSource.sort.sortChange, this.dataSource.paginator.page)
        .pipe(
          tap(() => this.loadLeads())
        )
        .subscribe();

      this.dataSource.paginator.page
        .pipe(
          tap(() => this.loadLeads())
        )
        .subscribe();
    }
    
  }

  ngOnDestroy(): void {
    if (this.dataSource.sort != null) {
      this.dataSource.sort.sortChange.unsubscribe();
      this.dataSource.paginator.page.unsubscribe();
    }
  }

  dealerManagement() {
    this.router.navigate(['/dealer']);
  }

  userManagement() {
    this.router.navigate(['/user']);
  }

  releaseManagement() {
    this.router.navigate(['/release']);
  }

  faqsManagement() {
    this.router.navigate(['/support']);
  }

  newLead() {
    this.router.navigate(['/lead/add']);
  }

  viewJobs() {
    this.router.navigate(['/job']);
  }

  viewQuotes() {
    this.router.navigate(['/quote']);
  }

  viewLead(quoteId) {
    this.router.navigate(['/lead/' + quoteId]);
  }

  get quoteZoneClass(): string {
    if (this.quoteOnly)
      return 'col-md-12 pl-0 pr-0';
    else
      return 'col-md-6 pl-0';
  }

  setupDates() {
    let today = new Date();

    this.customYear = today.getFullYear();
    this.customMonth = today.getMonth();

    for (let i = 2019; i <= today.getFullYear(); i++) {
      this.years.push(i);
    }
    for (let i = 1; i <= 12; i++) {
      let monthDate = new Date(null, i, null);

      this.months.push({
        MonthNum: i - 1,
        MonthName: monthDate.toLocaleString(navigator.language, { month: 'short' })
      } as MonthType);
    }
  }

  showDefaultDate(visibility: boolean) {
    this.defaultDate = visibility;
    this.currentMonth = true;
    this.loadDashboardSummary();
  }

  loadSummary() {
    this.homeService.getLeadSummary(this.leadFilter)
      .subscribe({
        next: (data) => {
          this.summarySection = data;
        },
        error: (error: string) => this.onError(error)
      });
  }

  loadDashboardSummary() {
    let today = new Date();

    if (!this.defaultDate) {
      today.setFullYear(this.customYear, this.customMonth);
    } else if (!this.currentMonth) {
      today.setMonth(today.getMonth() - 1);
    }

    let year = today.getFullYear();
    let month = today.getMonth();

    // Convert javascript 0-indexed month num to C# month num
    this.homeService.getDashboardSummary(year, month + 1)
      .subscribe({
        next: (data) => {
          this.summary = data;
        },
        error: (error: string) => this.onError(error)
      });
  };

  applyLeadFilter() {
    this.loadSummary();
    this.loadLeads();
  }

  loadLeads() {
    this.dataSource.loadLeads(this.leadFilter,
      this.dataSource.sort.active, this.dataSource.sort.direction, this.dataSource.paginator.pageIndex);
  };

  getReleaseNotification() {
    this.homeService.getReleaseNotification().subscribe({
      next: (data) => {
        let latestNotification: ReleaseSettings = data[0] as ReleaseSettings ?? {} as ReleaseSettings;
        this.notificationMessage = latestNotification.releaseSettings_ShowNotification ? latestNotification.releaseSettings_NotificationMessage : '';
      },
      error: (error: string) => this.onError(error)
    });
  }

  testNotifications() {
    this.homeService.testNotifications()
    .subscribe({
      next: (data) => {
        const x = data;
        console.log(x);
      },
      error: (error: string) => this.onError(error)
    });

  };
}
